import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.colors.opacityColor};

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1040px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;

  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s ease-out, transform 0.7s ease-out;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.95rem;
  background: ${({ theme }) => theme.colors.activeColor}14;
  color: ${({ theme }) => theme.colors.activeColor};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  border-radius: 999px;
  margin-bottom: 1.25rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 750;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.headline};
  margin: 0;
`;

const AccentText = styled.span`
  color: ${({ theme }) => theme.colors.activeColor};
`;

const Card = styled.div`
  border-radius: ${({ theme }) => theme.radii.lg};
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  background: ${({ theme }) => theme.colors.cardBG};
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  overflow: hidden;

  opacity: 0;
  transform: translateY(18px);
  transition: opacity 0.7s ease-out 0.12s, transform 0.7s ease-out 0.12s;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 760px;
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${({ theme }) => theme.colors.cardBG};
`;

const HeaderRow = styled.tr``;

const Th = styled.th`
  padding: 1rem 1.25rem;
  text-align: left;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.colors.subTextColor};
  white-space: nowrap;
  background: ${({ theme }) => theme.colors.cardBG};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};

  &:first-child {
    color: ${({ theme }) => theme.colors.headline};
    font-weight: 750;
  }
`;

const ThPlan = styled(Th)`
  color: ${({ theme }) => theme.colors.headline};
  background: ${({ theme }) => theme.colors.activeColor}08;
  box-shadow: inset 1px 0 0 ${({ theme }) => theme.colors.borderColor};
`;

const PlanBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 0.6rem;
  padding: 0.22rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.activeColor};
  background: ${({ theme }) => theme.colors.activeColor}14;
  border: 1px solid ${({ theme }) => theme.colors.activeColor}2A;
`;

const BodyRow = styled.tr`
  td:not(:last-child) {
    background: ${({ theme }) => theme.colors.cardBG};
  }

  &:nth-child(even) td:not(:last-child) {
    background: ${({ theme }) => theme.colors.opacityColor};
  }
  
  &:hover td:not(:last-child) {
    background: ${({ theme }) => theme.colors.opacityColor};
  }
`;

const Td = styled.td`
  padding: 1rem 1.25rem;
  font-size: 0.95rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.cardText};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};

  &:first-child {
    color: ${({ theme }) => theme.colors.headline};
    font-weight: 650;
    letter-spacing: -0.01em;
  }
`;

const TdPlan = styled(Td)`
  background: ${({ theme }) => theme.colors.activeColor}06 !important;
  color: ${({ theme }) => theme.colors.headline};
  font-weight: 650;
  box-shadow: inset 1px 0 0 ${({ theme }) => theme.colors.borderColor};
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.6rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 650;
  letter-spacing: -0.005em;
  border: 1px solid rgba(255,255,255,0.08);
  background: rgba(255,255,255,0.05);
  color: ${({ theme }) => theme.colors.cardText};
  white-space: nowrap;
`;

const ChipGood = styled(Chip)`
  border-color: ${({ theme }) => theme.colors.activeColor}30;
  background: ${({ theme }) => theme.colors.activeColor}12;
  color: ${({ theme }) => theme.colors.activeColor};
`;

const ChipBad = styled(Chip)`
  border-color: ${({ theme }) => theme.colors.borderColor};
  background: ${({ theme }) => theme.colors.opacityColor};
  color: ${({ theme }) => theme.colors.subTextColor};
  opacity: 0.7;
`;

const ChipNeutral = styled(Chip)`
  opacity: 0.95;
`;

const Footnote = styled.div`
  padding: 0.9rem 1.25rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.subTextColor};
  background: ${({ theme }) => theme.colors.opacityColor};
  border-top: 1px solid ${({ theme }) => theme.colors.borderColor};
`;

const rows = [
  { feature: 'Rent estimates', generic: 'National averages', spreadsheet: 'Manual entry', propninja: 'AI-powered per property' },
  { feature: 'CapEx reserves', generic: 'Flat 5%', spreadsheet: 'Whatever you type', propninja: 'Per-component by age' },
  { feature: 'Tax rates', generic: 'State average', spreadsheet: 'Manual entry', propninja: 'County-level, automatic' },
  { feature: 'Live MLS data', generic: 'No', spreadsheet: 'No', propninja: 'Real-time per ZIP' },
  { feature: 'Multi-property scan', generic: 'No', spreadsheet: 'One at a time', propninja: 'Entire ZIP at once' },
  { feature: 'Price', generic: 'Free–$50+/mo', spreadsheet: 'Free', propninja: 'Free tier, from $9.99' }
];

function renderValue(value, { highlight = false } = {}) {
  const v = String(value).trim().toLowerCase();

  if (v === 'no') return <ChipBad>✕ No</ChipBad>;
  if (v === 'yes') return <ChipGood>✓ Yes</ChipGood>;

  if (v.includes('manual')) return <ChipNeutral>Manual</ChipNeutral>;
  if (v.includes('automatic') || v.includes('real-time') || v.includes('ai-powered')) {
    return <ChipGood>{value}</ChipGood>;
  }

  if (highlight) return <ChipGood>{value}</ChipGood>;
  return <Chip>{value}</Chip>;
}

const ComparisonTable = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    const fallback = setTimeout(() => setIsVisible(true), 3000);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <Section ref={sectionRef}>
      <Container>
        <SectionHeader className={isVisible ? 'visible' : ''}>
          <SectionLabel>How We Compare</SectionLabel>
          <SectionTitle>
            Not Another <AccentText>Spreadsheet</AccentText>
          </SectionTitle>
        </SectionHeader>

        <Card className={isVisible ? 'visible' : ''}>
          <TableScroll>
            <Table>
              <Thead>
                <HeaderRow>
                  <Th>Feature</Th>
                  <Th>Generic Calculators</Th>
                  <Th>Spreadsheets</Th>
                  <ThPlan>PropNinja <PlanBadge>Recommended</PlanBadge></ThPlan>
                </HeaderRow>
              </Thead>
              <tbody>
                {rows.map((row) => (
                  <BodyRow key={row.feature}>
                    <Td>{row.feature}</Td>
                    <Td>{renderValue(row.generic)}</Td>
                    <Td>{renderValue(row.spreadsheet)}</Td>
                    <TdPlan>{renderValue(row.propninja, { highlight: true })}</TdPlan>
                  </BodyRow>
                ))}
              </tbody>
            </Table>
          </TableScroll>

          <Footnote>
            *Comparison highlights typical workflows. Exact results vary by market, listing data, and assumptions.
          </Footnote>
        </Card>
      </Container>
    </Section>
  );
};

export default ComparisonTable;
